import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Loader2, ShoppingBag, Timer, X, XCircle, UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useProductResgatePreview, useGenerateResgateToken, useCancelResgateToken } from "@/hooks/useResgates";
import { useRequestMembership } from "@/hooks/useCompanies";
import type { ResgateTokenComCode } from "@/types/api";

function fmtPts(n: number) {
  return n.toLocaleString("pt-BR");
}

function fmtCodigo(code: string) {
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

function Countdown({ expiresAt }: { expiresAt: string }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return (
    <span className={remaining < 60 ? "text-destructive font-semibold" : "text-muted-foreground"}>
      {m}:{String(s).padStart(2, "0")}
    </span>
  );
}

type NoCarteiraDetail = { code: "no_carteira"; company_id: number; message: string };

function parseErrorDetail(err: unknown): NoCarteiraDetail | string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === "object" && (detail as NoCarteiraDetail).code === "no_carteira") {
    return detail as NoCarteiraDetail;
  }
  return typeof detail === "string" ? detail : "Produto não encontrado ou indisponível.";
}

export default function ShopProductResgate() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
  const [activeToken, setActiveToken] = useState<ResgateTokenComCode | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: `/resgate/loja/${productId}` } });
    }
  }, [authLoading, isAuthenticated, loginWithRedirect, productId]);

  const {
    data: preview,
    isLoading: previewLoading,
    isError: previewError,
    error: previewErrorDetail,
  } = useProductResgatePreview(isAuthenticated && productId ? productId : "");

  const generateToken = useGenerateResgateToken();
  const cancelToken = useCancelResgateToken();
  const requestMembership = useRequestMembership();

  function handleConfirm() {
    if (!preview) return;
    generateToken.mutate(
      {
        valor: String(preview.price_points),
        tipo: "pontos",
        carteira_id: preview.carteira_id,
        product_id: preview.product_id,
      },
      {
        onSuccess: (token) => setActiveToken(token),
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : "Erro ao gerar código.";
          toast.error(msg);
        },
      },
    );
  }

  function handleCancel() {
    if (!activeToken) return;
    cancelToken.mutate(activeToken.id, {
      onSuccess: () => {
        setActiveToken(null);
        toast.info("Código cancelado.");
      },
    });
  }

  function handleRequestMembership(companyId: number) {
    requestMembership.mutate(companyId, {
      onSuccess: () => {
        setRequestSent(true);
        toast.success("Solicitação enviada com sucesso!");
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Erro ao enviar solicitação.";
        toast.error(msg);
      },
    });
  }

  if (authLoading || (!isAuthenticated && !authLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (previewLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (previewError) {
    const parsed = parseErrorDetail(previewErrorDetail);
    const isNoCarteira = typeof parsed === "object" && parsed.code === "no_carteira";

    if (isNoCarteira && requestSent) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <Card className="max-w-md w-full text-center">
            <CardHeader>
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-2" />
              <CardTitle>Solicitação enviada</CardTitle>
              <CardDescription>
                O administrador da loja foi notificado e irá revisar sua solicitação. Assim que for aceito, você poderá resgatar produtos aqui.
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                Ir para o início
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    if (isNoCarteira) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <Card className="max-w-md w-full text-center">
            <CardHeader>
              <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <CardTitle>Você não é membro desta loja</CardTitle>
              <CardDescription>
                Para resgatar produtos aqui, você precisa ser convidado ou solicitar acesso ao administrador da loja.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full gap-2"
                onClick={() => handleRequestMembership((parsed as NoCarteiraDetail).company_id)}
                disabled={requestMembership.isPending}
              >
                {requestMembership.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                Solicitar acesso a esta loja
              </Button>
            </CardContent>
            <CardFooter className="justify-center">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                Cancelar
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>Produto não disponível</CardTitle>
            <CardDescription>{typeof parsed === "string" ? parsed : parsed.message}</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Ir para o início
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (activeToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full border-primary/40 overflow-hidden">
          {preview?.product_image_url && (
            <div className="w-full h-36 bg-muted">
              <img
                src={preview.product_image_url}
                alt={preview.product_name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold tracking-wider">
              Código de Resgate
            </CardDescription>
            <CardTitle className="text-4xl tracking-[0.25em] text-center py-4 font-mono">
              {fmtCodigo(activeToken.code)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between text-sm rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Timer className="h-4 w-4" />
                Válido por
              </div>
              <Countdown expiresAt={activeToken.expires_at} />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              <span className="font-semibold">{preview?.product_name}</span>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Mostre este código ao vendedor para confirmar o resgate.
            </p>
            <Separator />
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 text-destructive hover:text-destructive"
              onClick={handleCancel}
              disabled={cancelToken.isPending}
            >
              {cancelToken.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Cancelar código
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="max-w-md w-full overflow-hidden">
        {preview?.product_image_url ? (
          <div className="w-full h-52 bg-muted">
            <img
              src={preview.product_image_url}
              alt={preview.product_name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-32 bg-primary/10 flex items-center justify-center">
            <ShoppingBag className="h-12 w-12 text-primary/40" />
          </div>
        )}
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">{preview?.product_name}</CardTitle>
          {preview?.product_description && (
            <CardDescription>{preview.product_description}</CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-4 pt-4">
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Custo</span>
              <span className="font-semibold">{fmtPts(preview?.price_points ?? 0)} pts</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Seu saldo</span>
              <span className="font-semibold">{fmtPts(parseInt(preview?.saldo_pontos ?? "0"))} pts</span>
            </div>
          </div>

          {!preview?.is_sufficient && (
            <p className="text-center text-sm text-destructive font-medium">
              Saldo insuficiente para este produto.
            </p>
          )}
        </CardContent>

        <CardFooter className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => navigate("/")}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!preview?.is_sufficient || generateToken.isPending}
          >
            {generateToken.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Confirmar Resgate"
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
